import React, { useEffect, useRef, useState } from 'react';
import { Box, Grid, IconButton, Modal } from '@mui/material';
import InputField from '../../../Components/Input';
import { ImageCropperInput } from '../../../Components/ImageCropperInput';
import CloseIcon from '@mui/icons-material/Close';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { AddStudioSchema } from '../../../Utils/yupvalidations';
import { notify } from '../../../Utils/toastify';
import { fetchLocation, useDebounce } from '../../../Utils/helperFns';
import { useDispatch } from 'react-redux';
import {
  addStudio,
  getAllStudiosList,
  uniqueStudioCheckMethod,
} from '../../../redux/StudioSlice';
import CustomButton from '../../../Components/CustomButton';
import AutoCompleteLocation from '../../../Components/AutoCompleteLocation';

const StuioCreate = ({
  open,
  handleClose,
  setValueofStudio,
  getValuesStudio,
}: any) => {
  const [base64File, setBase64File] = useState('');
  const [latLong, setLatLong] = useState<any>(null);

  const [load, setLoad] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  const dispatch: any = useDispatch();

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    setValue,
    watch,
    getValues,
    clearErrors,
    reset,
    trigger,
  } = useForm({
    resolver: yupResolver(AddStudioSchema),
    mode: 'all',
  });

  const [zipCode, setZipCode] = useState('');

  const debouncedSearchTerm = useDebounce(zipCode, 1000);

  // const zipCodeWatch = watch('zipCode')

  const fetchAddressInfo = async (code: string): Promise<void> => {
    setLoad(true);
    try {
      const data = await fetchLocation(code);

      const addressComponents = data.results[0]?.address_components || [];
      const geoLocation = data.results[0]?.geometry || null;
      const [addressLine1, ...rest] =
        data.results?.formatted_address.split(',');
      // const locationName =
      //   newValue?.structured_formatting?.main_text || newValue?.address;

      let addressHolder = '';

      for (const component of results.address_components) {
        if (component.types?.includes('street_number')) {
          if (addressHolder.length) {
            addressHolder = `${component.long_name} ${addressHolder}`;
          } else {
            addressHolder = component.long_name;
          }
        } else if (component.types.includes('route')) {
          addressHolder = `${addressHolder} ${component.long_name}`;
        }
      }

      if (addressHolder.length) {
        setValue('addressLine1', addressHolder);
      }

      if (geoLocation) {
        setValue('latitude', geoLocation.location.lat);
        setValue('longitude', geoLocation.location.lng);
      } else {
        setValue('latitude', '');
        setValue('longitude', '');
      }
      const city = addressComponents.find(
        (component: any) =>
          component.types.includes('locality') ||
          component.types.includes('political'),
      )?.long_name;
      const state = addressComponents.find((component: any) =>
        component.types.includes('administrative_area_level_1'),
      )?.long_name;
      const country = addressComponents.find((component: any) =>
        component.types.includes('country'),
      )?.long_name;
      if (city) {
        setValue('city', city);
        clearErrors('city');
      } else {
        setValue('city', '');
      }
      if (state) {
        setValue('state', state);
        clearErrors('state');
      } else {
        setValue('state', '');
      }
      // if (addressLine1) {
      //   setValue('addressLine1', addressLine1);
      //   clearErrors('addressLine1');
      // } else {
      //   setValue('addressLine1', '');
      // }

      if (country) {
        setValue('country', country);
        if (country === 'United States') {
          //   getAndSetHolidayList()
        }
      }
      //   clearErrors('country');
      // } else {
      //   setValue('country', '');
      // }
      setLoad(false);
    } catch (error) {
      setLoad(false);
    }
  };

  useEffect(() => {
    if (zipCode) {
      fetchAddressInfo(zipCode);
    }
  }, [debouncedSearchTerm]);

  const handleOnkeyBlur = (event: any) => {
    if (!event.target.value) {
      setValue('state', '');
      setValue('city', '');
      // setValue('country', '');
    }
    if (event.target.value.length > 3) {
      setZipCode(event.target.value);
    }
  };

  const setDetails = (results: any, newValue: any) => {
    if (!results || !results.address_components) {
      // Reset fields if no results are available
      setValue('city', '');
      setValue('state', '');
      // setValue('country', '');
      setValue('zipCode', '');
      setValue('addressLine2', '');
      setValue('addressLine1', '');
      setValue('latitude', '');
      setValue('longitude', '');
      setValue('locationName', '');
      return;
    }

    const addressComponents = results.address_components;

    const getAddressComponent = (type: string) => {
      return (
        addressComponents?.find((component: any) =>
          component.types.includes(type),
        )?.long_name || ''
      );
    };

    const country = getAddressComponent('country');
    const state = getAddressComponent('administrative_area_level_1');
    const city =
      getAddressComponent('locality') ||
      getAddressComponent('sublocality') ||
      getAddressComponent('postal_town') || // Used in some countries
      getAddressComponent('administrative_area_level_2'); // Fallback
    const zipCode = getAddressComponent('postal_code');
    const [addressLine1, ...rest] = results?.formatted_address.split(',');
    const addressLine2 = getAddressComponent('subpremise');
    const locationName =
      newValue?.structured_formatting?.main_text || newValue?.address;

    let addressHolder = '';

    for (const component of results.address_components) {
      if (component.types?.includes('street_number')) {
        if (addressHolder.length) {
          addressHolder = `${component.long_name} ${addressHolder}`;
        } else {
          addressHolder = component.long_name;
        }
      } else if (component.types.includes('route')) {
        addressHolder = `${addressHolder} ${component.long_name}`;
      }
    }

    if (addressHolder.length) {
      setValue('addressLine1', addressHolder);
    }

    setValue('city', city);
    setValue('state', state);
    setValue('country', country);
    setValue('zipCode', zipCode);
    setValue('locationName', locationName);
    setValue('addressLine2', addressLine2 || '');
    setValue('latitude', results?.geometry?.location?.lat());
    setValue('longitude', results?.geometry?.location?.lng());

    // Clear errors if values are set
    if (city) clearErrors('city');
    if (state) clearErrors('state');
    // if (country) clearErrors('country');
    if (zipCode) clearErrors('zipCode');
    if (addressLine2) clearErrors('addressLine2');
    if (locationName) clearErrors('locationName');
  };

  const onSubmit = (data: any) => {
    // console.log(data)

    // if (!base64File) {
    //   notify('Please select image.', 'error')
    //   return
    // }

    const payload = {
      name: data.name,
      ...(base64File && { image: base64File }),
      address: {
        locationName: data.address?.description,
        city: data.city,
        country: data.country,
        zipCode: data.zipCode,
        state: data.state,
        latitude: data?.latitude,
        longitude: data?.longitude,
        address: data?.addressLine1,
        ...(data?.addressLine2 && { addressLine2: data.addressLine2 }),
      },
    };

    dispatch(
      addStudio(payload, (data: any) => {
        // console.log('DDDD=>', data)
        setValueofStudio('studio', [
          ...getValuesStudio()?.studio,
          { name: data?.name, _id: data?._id },
        ]);
        setBase64File('');
        reset();
        handleClose();
        getAllStudiosList();
      }),
    );

    // handleClose()
  };

  const handleFocusStudio = () => {
    uniqueStudioCheckMethod(getValues('name'), (data: any) => {
      // console.log(data)
      if (data === 'This name already exists.') {
        notify('This studio name already exists.', 'error');
        setValue('name', '');
        // setError('name', {
        //   type: 'manual',
        //   message: 'This name already exists.',
        // })
      }
    });
  };

  console.log('ERRR =>', errors, isValid);

  return (
    <div>
      <Modal
        open={open}
        // onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        className="modal-main"
      >
        <form ref={formRef} onSubmit={handleSubmit(onSubmit)}>
          <div className="modal-dialog med">
            <div className="modal-content">
              <div className="modal-title d-flex flex-between align-center">
                <p className="title text-gray_700 fw500">Create Studio</p>
                <button
                  className="blank-btn"
                  onClick={() => {
                    clearErrors();
                    reset();
                    handleClose();
                  }}
                >
                  <i className="cross-icon"></i>
                </button>
              </div>

              <div className="modal-body modal_scroll pt-20">
                <Grid container spacing={2}>
                  <Grid item md={6} xs={12}>
                    <InputField
                      requiredField
                      id="Studio"
                      label="Studio Name"
                      maxValue={50}
                      name="name"
                      control={control}
                      placeholder="Enter Name"
                      error={!!errors['name']}
                      helperText={
                        !!errors['name'] ? errors['name'].message : ''
                      }
                      onBlurMethod={() => {
                        handleFocusStudio();
                      }}
                    />
                  </Grid>

                  <Grid item md={6} xs={12}>
                    {/* <InputField
                      id="Address1"
                      label="Address"
                      name="address"
                      control={control}
                      placeholder="Enter Address"
                      error={!!errors['address']}
                      helperText={
                        !!errors['address'] ? errors['address'].message : ''
                      }
                      requiredField
                    /> */}

                    <AutoCompleteLocation
                      requiredField
                      labelText="Location"
                      name="address"
                      control={control}
                      setValue={setValue}
                      placeHolderText="Select address"
                      limit={100}
                      setLatLong={setLatLong}
                      changeCallBack={(result: any, newValue: any) => {
                        setDetails(result, newValue);
                        trigger('address');
                      }}
                      defaultValue={''}
                      isObject={true}
                      error={!!errors['address']}
                      helperText={
                        !!errors['address'] ? errors['address'].message : ''
                      }
                    />
                  </Grid>
                  <Grid item md={6} xs={12}>
                    <InputField
                      id="addressline1"
                      requiredField
                      label="Location Name"
                      name="locationName"
                      placeholder="Enter location name"
                      control={control}
                      error={!!errors['locationName']}
                      helperText={
                        !!errors['locationName']
                          ? errors['locationName'].message
                          : ''
                      }
                      // onKeyUp={handleOnkeyBlur}
                      // requiredField
                    />
                  </Grid>
                  <Grid item md={6} xs={12}>
                    <InputField
                      id="addressline1"
                      requiredField
                      label="Address Line 1"
                      name="addressLine1"
                      placeholder="Enter address line 1"
                      control={control}
                      error={!!errors['addressLine1']}
                      helperText={
                        !!errors['addressLine1']
                          ? errors['addressLine1'].message
                          : ''
                      }
                      // onKeyUp={handleOnkeyBlur}
                      // requiredField
                    />
                  </Grid>
                  <Grid item md={6} xs={12}>
                    <InputField
                      id="addressline2"
                      label="Address Line 2"
                      name="addressLine2"
                      placeholder="Enter address line 2"
                      control={control}
                      error={!!errors['addressLine2']}
                      helperText={
                        !!errors['addressLine2']
                          ? errors['addressLine2'].message
                          : ''
                      }
                      // onKeyUp={handleOnkeyBlur}
                      // requiredField
                    />
                  </Grid>

                  <Grid item md={6} xs={12}>
                    <InputField
                      id="Postal"
                      label="Postal Code"
                      name="zipCode"
                      type="number"
                      placeholder="Enter zip / postal code"
                      control={control}
                      error={!!errors['zipCode']}
                      helperText={
                        !!errors['zipCode'] ? errors['zipCode'].message : ''
                      }
                      // onKeyUp={handleOnkeyBlur}
                      requiredField
                    />
                  </Grid>
                  <Grid item md={6} xs={12}>
                    <InputField
                      id="City"
                      label="City/Town"
                      name="city"
                      placeholder="Enter city"
                      control={control}
                      error={!!errors['city']}
                      helperText={
                        !!errors['city'] ? errors['city'].message : ''
                      }
                      requiredField
                    />
                  </Grid>

                  <Grid item md={6} xs={12}>
                    <InputField
                      id="State"
                      label="State"
                      name="state"
                      placeholder="Enter state"
                      control={control}
                      error={!!errors['state']}
                      helperText={
                        !!errors['state'] ? errors['state'].message : ''
                      }
                      requiredField
                    />
                  </Grid>

                  {/* <Grid item md={6} xs={12}>
                    <InputField
                      id="City"
                      label="Country"
                      name="country"
                      placeholder="Enter country"
                      control={control}
                      error={!!errors['country']}
                      helperText={
                        !!errors['country'] ? errors['country'].message : ''
                      }
                      requiredField
                    />
                  </Grid> */}
                  <Grid item md={6} xs={12}>
                    <InputField
                      requiredField
                      disabled
                      id="Latitude"
                      label="Latitude"
                      name="latitude"
                      placeholder="Enter Latitude"
                      error={!!errors.latitude}
                      helperText={errors.latitude?.message}
                      control={control}
                    />
                  </Grid>

                  <Grid item md={6} xs={12}>
                    <InputField
                      requiredField
                      disabled
                      id="Longitude"
                      label="Longitude"
                      name="longitude"
                      placeholder="Enter Longitude"
                      error={!!errors.longitude}
                      helperText={errors.longitude?.message}
                      control={control}
                    />
                  </Grid>

                  {/* <Grid item md={6} xs={12}></Grid> */}
                </Grid>
                <Grid item md={6} xs={12}>
                  <div className="imageContainer">
                    {!base64File && (
                      <ImageCropperInput
                        setLoad={setLoad}
                        setProgress={setUploadProgress}
                        setUrl={setBase64File}
                        loading={load}
                        ratio={2 / 1}
                        title="Add Image"
                        uploadProgress={uploadProgress}
                        onlyImage={true}
                      />
                    )}
                    {base64File && (
                      <div className="">
                        <div className="imageItem ">
                          <Box className="selectimg_video">
                            {/* {base64File?.length > 80 && ( */}

                            <IconButton
                              className="removeImage"
                              onClick={() => {
                                setBase64File('');

                                // reset({ file: '' })
                              }}
                            >
                              <CloseIcon />
                            </IconButton>

                            {/* )} */}
                            <figure className="selected">
                              <img src={base64File} alt={'image'} />
                            </figure>
                          </Box>
                        </div>
                      </div>
                    )}
                  </div>
                </Grid>
              </div>

              <div className="modal-actions">
                <div>
                  <CustomButton
                    id=""
                    text="Cancel"
                    variant="outlined"
                    showIcon={false}
                    className="min_width_btn"
                    onClick={() => {
                      clearErrors();
                      reset();
                      handleClose();
                    }}
                  />
                  {/* <button
                    className="btn-white"
                    onClick={() => {
                      clearErrors()
                      reset()
                      handleClose()
                    }}
                  >
                    Cancel
                  </button> */}
                </div>
                <div>
                  <CustomButton
                    id=""
                    text="Create"
                    variant="contained"
                    disabled={!isValid}
                    showIcon={false}
                    type="submit"
                    className="min_width_btn"
                    // onClick={() => {
                    //   clearErrors()
                    //   reset()
                    //   handleClose()
                    // }}
                  />
                  {/* <button className="btn">Continue</button> */}
                </div>
              </div>
            </div>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default StuioCreate;
